import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Space, Typography, Alert } from "antd";
import axios from "axios";

const { Title, Text } = Typography;

const ReorderPointSafetyStock = ({
    fileId,
    fileName,
    setSelectedFile,
    initialConfig,
}) => {
    const [activeTab, setActiveTab] = useState("input");
    const [inputData, setInputData] = useState({
        reorder_point: 0,
        carrying_cost: 0,
        stockout_cost: 0,
        orders_per_year: 0,
        demand_distribution: [], // array of [demand, probability]
    });
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const getAuthToken = () => localStorage.getItem("access_token");

    useEffect(() => {
        if (fileId) {
            loadData(fileId);
        } else if (initialConfig) {
            // initialConfig has demand_levels and probabilities from modal
            const dist = initialConfig.demand_levels.map((d, i) => [d || 0, initialConfig.probabilities[i] || 0]);
            setInputData({
                reorder_point: 200,
                carrying_cost: 2.5,
                stockout_cost: 50,
                orders_per_year: 12,
                demand_distribution: dist,
            });
        }
    }, [fileId, initialConfig]);

    const loadData = async (id) => {
        try {
            const token = getAuthToken();
            if (!token) return message.error("Not authenticated.");
            setIsLoading(true);

            const res = await axios.get(
                `/api/retrieve_reorder_point_safety_stock/${id}/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setInputData(res.data.input_data);
            setResults(res.data.output_data?.output_data || res.data.output_data);
            setIsSaved(true);
            setActiveTab("output");
        } catch (err) {
            console.error("Load error:", err);
            message.error("Failed to load data");
        } finally {
            setIsLoading(false);
        }
    };

    const saveData = async () => {
        try {
            const token = getAuthToken();
            if (!token) return message.error("Not authenticated.");
            setIsLoading(true);

            const payload = {
                name: fileName || "Reorder Point Safety Stock",
                input_data: inputData,
            };

            let res;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            if (fileId) {
                res = await axios.put(
                    `/api/update_reorder_point_safety_stock/${fileId}/`,
                    payload,
                    config
                );
            } else {
                res = await axios.post(
                    `/api/save_reorder_point_safety_stock/`,
                    payload,
                    config
                );
            }

            setResults(res.data.output_data?.output_data || res.data.output_data);
            setIsSaved(true);
            setActiveTab("output");
            message.success(fileId ? "Updated successfully" : "Saved successfully");
        } catch (err) {
            console.error("Save error:", err.response?.data || err.message);
            const errorMsg = err.response?.data?.error || err.message;
            message.error(`Failed to save: ${Array.isArray(errorMsg) ? errorMsg.join(", ") : errorMsg}`);
        } finally {
            setIsLoading(false);
        }
    };

    const updateDemand = (index, value) => {
        const newDist = [...inputData.demand_distribution];
        newDist[index][0] = value;
        setInputData({ ...inputData, demand_distribution: newDist });
    };

    const updateProbability = (index, value) => {
        const newDist = [...inputData.demand_distribution];
        newDist[index][1] = value;
        setInputData({ ...inputData, demand_distribution: newDist });
    };

    const addRow = () => {
        setInputData({
            ...inputData,
            demand_distribution: [...inputData.demand_distribution, [0, 0]],
        });
    };

    const removeRow = (index) => {
        const newDist = inputData.demand_distribution.filter((_, i) => i !== index);
        setInputData({ ...inputData, demand_distribution: newDist });
    };

    const renderInputTable = () => (
        <Card title="Input Parameters">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Table
                    bordered
                    pagination={false}
                    showHeader={false}
                    columns={[
                        { title: "Param", dataIndex: "param", width: "40%" },
                        { title: "Value", dataIndex: "value" },
                    ]}
                    dataSource={[
                        {
                            key: "rp",
                            param: "Original Reorder Point",
                            value: (
                                <InputNumber
                                    style={{ width: "100%" }}
                                    value={inputData.reorder_point}
                                    onChange={(v) => setInputData({ ...inputData, reorder_point: v })}
                                />
                            ),
                        },
                        {
                            key: "cc",
                            param: "Carrying Cost per unit/year",
                            value: (
                                <InputNumber
                                    style={{ width: "100%" }}
                                    value={inputData.carrying_cost}
                                    onChange={(v) => setInputData({ ...inputData, carrying_cost: v })}
                                />
                            ),
                        },
                        {
                            key: "sc",
                            param: "Stockout Cost per unit",
                            value: (
                                <InputNumber
                                    style={{ width: "100%" }}
                                    value={inputData.stockout_cost}
                                    onChange={(v) => setInputData({ ...inputData, stockout_cost: v })}
                                />
                            ),
                        },
                        {
                            key: "oy",
                            param: "Number of Orders per Year",
                            value: (
                                <InputNumber
                                    style={{ width: "100%" }}
                                    value={inputData.orders_per_year}
                                    onChange={(v) => setInputData({ ...inputData, orders_per_year: v })}
                                />
                            ),
                        },
                    ]}
                />

                <Title level={5}>Demand Distribution</Title>
                <Table
                    bordered
                    pagination={false}
                    size="small"
                    dataSource={inputData.demand_distribution.map(([d, p], i) => ({
                        key: i,
                        demand: d,
                        probability: p,
                    }))}
                    columns={[
                        {
                            title: "Demand during Lead Time",
                            dataIndex: "demand",
                            render: (val, _, i) => (
                                <InputNumber
                                    style={{ width: "100%" }}
                                    value={val}
                                    onChange={(v) => updateDemand(i, v)}
                                />
                            ),
                        },
                        {
                            title: "Probability",
                            dataIndex: "probability",
                            render: (val, _, i) => (
                                <InputNumber
                                    style={{ width: "100%" }}
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    value={val}
                                    onChange={(v) => updateProbability(i, v)}
                                />
                            ),
                        },
                        {
                            title: "Action",
                            render: (_, __, i) => (
                                <Button type="link" danger onClick={() => removeRow(i)}>
                                    Remove
                                </Button>
                            ),
                        },
                    ]}
                />
                <Button onClick={addRow}>Add Demand Level</Button>

                <Alert
                    message={`Total Probability: ${inputData.demand_distribution.reduce((acc, curr) => acc + (curr[1] || 0), 0).toFixed(4)}`}
                    type={Math.abs(inputData.demand_distribution.reduce((acc, curr) => acc + (curr[1] || 0), 0) - 1.0) < 0.0001 ? "success" : "warning"}
                />

                <Button type="primary" size="large" onClick={saveData} loading={isLoading}>
                    {isSaved ? "Update Analysis" : "Save & Calculate"}
                </Button>
            </Space>
        </Card>
    );

    const renderOutputTable = () => {
        if (!results) return <Alert message="Please calculate results first" type="info" showIcon />;

        return (
            <Card title="Analysis Results">
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Table
                        bordered
                        pagination={false}
                        columns={[
                            { title: "Parameter", dataIndex: "param", width: "60%" },
                            { title: "Value", dataIndex: "value", align: "right" },
                        ]}
                        dataSource={[
                            { key: "1", param: "Optimal Safety Stock Level", value: results.safety_stock },
                            { key: "2", param: "Annual Holding Cost", value: `$${results.holding_cost?.toLocaleString() || results.holding_cost}` },
                            { key: "3", param: "Annual Expected Stockout Cost", value: `$${results.stockout_cost?.toLocaleString() || results.stockout_cost}` },
                            { key: "4", param: "Total Annual Cost", value: <strong>{`$${results.total_cost?.toLocaleString() || results.total_cost}`}</strong> },
                            { key: "5", param: "Revised Reorder Point", value: results.revised_reorder_point },
                        ]}
                    />
                    <Card type="inner" title="Conclusion">
                        <Text>
                            To minimize total annual costs, a safety stock of <strong>{results.safety_stock}</strong> units should be maintained.
                            The new reorder point is <strong>{results.revised_reorder_point}</strong> units.
                        </Text>
                    </Card>
                </Space>
            </Card>
        );
    };

    return (
        <Card title={fileName || "Reorder Point / Safety Stock (Discrete Distribution)"}>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    { key: "input", label: "Input Parameters", children: renderInputTable() },
                    { key: "output", label: "Results", children: renderOutputTable(), disabled: !results },
                ]}
            />
        </Card>
    );
};

export default ReorderPointSafetyStock;
